/**
 * PDP Gallery — Modern (pgm-*)
 *
 * Drives the gallery in sections/product-info-modern-new.liquid. The buy box,
 * variant picker, Inquire modal and sticky bar are still run by
 * product-info-new.js; its gallery code looks for data-gallery-* hooks that
 * this markup doesn't have, so it finds nothing and returns early.
 *
 * Everything is scoped to one [data-pgm] root, so several sections on a page
 * never share state. Vanilla JS, no dependencies.
 */
(function () {
  'use strict';

  var DESKTOP = window.matchMedia('(min-width: 990px)');
  var FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function behavior() {
    return REDUCED.matches ? 'auto' : 'smooth';
  }

  function pad(n, len) {
    var s = String(n);
    while (s.length < len) s = '0' + s;
    return s;
  }

  function onMediaChange(mql, fn) {
    if (mql.addEventListener) mql.addEventListener('change', fn);
    else if (mql.addListener) mql.addListener(fn);
  }

  function offMediaChange(mql, fn) {
    if (mql.removeEventListener) mql.removeEventListener('change', fn);
    else if (mql.removeListener) mql.removeListener(fn);
  }

  // Scroll-end: native where supported, a quiet-period timer otherwise.
  function afterScroll(el, fn) {
    var done = false;
    var timer;
    function finish() {
      if (done) return;
      done = true;
      clearTimeout(timer);
      el.removeEventListener('scrollend', finish);
      el.removeEventListener('scroll', bump);
      fn();
    }
    function bump() {
      clearTimeout(timer);
      timer = setTimeout(finish, 140);
    }
    if ('onscrollend' in window) el.addEventListener('scrollend', finish);
    el.addEventListener('scroll', bump);
    timer = setTimeout(finish, 700);
  }

  // Pause any media playing inside `el` (HTML5 video, YouTube, Vimeo).
  function pauseMedia(el) {
    el.querySelectorAll('video').forEach(function (v) {
      if (!v.paused) v.pause();
    });
    el.querySelectorAll('iframe').forEach(function (frame) {
      if (!frame.contentWindow) return;
      var src = frame.getAttribute('src') || '';
      if (src.indexOf('youtube') !== -1) {
        frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      } else if (src.indexOf('vimeo') !== -1) {
        frame.contentWindow.postMessage('{"method":"pause"}', '*');
      }
    });
  }

  /* ======================================================================
   * Gallery instance
   * ==================================================================== */

  function Gallery(root) {
    this.root = root;
    this.section = root.closest('[data-product-info-section]') || root.parentElement;
    this.viewport = root.querySelector('[data-pgm-viewport]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-pgm-slide]'));
    this.thumbsWrap = root.querySelector('[data-pgm-thumbs]');
    this.thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-pgm-thumb]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-pgm-dot]'));
    this.currents = Array.prototype.slice.call(root.querySelectorAll('[data-pgm-current]'));
    this.live = root.querySelector('[data-pgm-live]');
    this.prevBtn = root.querySelector('[data-pgm-prev]');
    this.nextBtn = root.querySelector('[data-pgm-next]');
    this.isGrid = root.getAttribute('data-pgm-layout') === 'grid';
    this.zoomEnabled = root.getAttribute('data-pgm-zoom') === 'true';
    this.active = 0;
    this.ratios = [];
    this.cleanups = [];

    if (!this.viewport || !this.slides.length) return;

    this.variantMedia = this.readVariantMap();
    this.lightbox = new Lightbox(root, this);

    this.bindNavigation();
    this.bindObserver();
    this.bindDrag();
    this.bindZoom();
    this.bindOpen();
    this.bindVariants();
    this.bindStickyBuyBox();
    this.render();
    this.jumpToUrlVariant();
  }

  Gallery.prototype.listen = function (el, type, fn, opts) {
    el.addEventListener(type, fn, opts);
    this.cleanups.push(function () {
      el.removeEventListener(type, fn, opts);
    });
  };

  // Desktop grid is a static layout; everything else is a carousel.
  Gallery.prototype.isCarousel = function () {
    return !(this.isGrid && DESKTOP.matches);
  };

  Gallery.prototype.count = function () {
    return this.slides.length;
  };

  Gallery.prototype.indexOfMedia = function (mediaId) {
    for (var i = 0; i < this.slides.length; i++) {
      if (String(this.slides[i].getAttribute('data-media-id')) === String(mediaId)) return i;
    }
    return -1;
  };

  /* ---------- moving ---------- */

  Gallery.prototype.goTo = function (index, instant) {
    var n = this.count();
    if (!n) return;
    index = Math.max(0, Math.min(n - 1, index));
    var slide = this.slides[index];

    if (this.isCarousel()) {
      // Hold the observer off while we glide, or a multi-slide jump would
      // flash every slide it passes through on the thumbs and counter.
      var self = this;
      var token = (this.lockToken || 0) + 1;
      this.lockToken = token;
      this.locked = true;
      afterScroll(this.viewport, function () {
        if (self.lockToken === token) self.locked = false;
      });
      this.viewport.scrollTo({
        left: index * this.viewport.clientWidth,
        behavior: instant ? 'auto' : behavior()
      });
    } else {
      // Desktop grid: bring the item into view only if it is mostly off-screen.
      var rect = slide.getBoundingClientRect();
      var vh = window.innerHeight;
      var visible = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
      if (visible < rect.height * 0.5) {
        window.scrollTo({
          top: window.pageYOffset + rect.top - 24,
          behavior: instant ? 'auto' : behavior()
        });
      }
    }
    // Update immediately; the observer confirms once the scroll settles.
    this.setActive(index);
  };

  Gallery.prototype.step = function (dir) {
    this.goTo(this.active + dir);
  };

  Gallery.prototype.setActive = function (index) {
    if (index === this.active && this.rendered) return;
    var prev = this.slides[this.active];
    this.active = index;
    if (prev && prev !== this.slides[index]) {
      pauseMedia(prev);
      this.resetZoom(prev);
    }
    this.render();
  };

  Gallery.prototype.render = function () {
    var i = this.active;
    var n = this.count();
    this.rendered = true;

    this.slides.forEach(function (s, k) {
      s.classList.toggle('is-active', k === i);
    });
    this.dots.forEach(function (d, k) {
      d.classList.toggle('is-active', k === i);
    });
    this.thumbs.forEach(function (t, k) {
      var on = k === i;
      t.classList.toggle('is-active', on);
      if (on) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
    this.currents.forEach(function (el) {
      var len = parseInt(el.getAttribute('data-pgm-pad'), 10) || 0;
      el.textContent = pad(i + 1, len);
    });
    if (this.prevBtn) this.prevBtn.disabled = i <= 0;
    if (this.nextBtn) this.nextBtn.disabled = i >= n - 1;
    if (this.live && n > 1) this.live.textContent = 'Image ' + (i + 1) + ' of ' + n;

    this.followThumb();
  };

  // Keep the active thumb in view by scrolling the row only (never the page).
  Gallery.prototype.followThumb = function () {
    var wrap = this.thumbsWrap;
    var thumb = this.thumbs[this.active];
    if (!wrap || !thumb || wrap.scrollWidth <= wrap.clientWidth) return;
    var left = thumb.offsetLeft;
    var right = left + thumb.offsetWidth;
    var target = null;
    if (left < wrap.scrollLeft) target = left - 10;
    else if (right > wrap.scrollLeft + wrap.clientWidth) target = right - wrap.clientWidth + 10;
    if (target !== null) wrap.scrollTo({ left: target, behavior: behavior() });
  };

  /* ---------- arrows, thumbs, keyboard ---------- */

  Gallery.prototype.bindNavigation = function () {
    var self = this;
    if (this.prevBtn) {
      this.listen(this.prevBtn, 'click', function () {
        self.step(-1);
      });
    }
    if (this.nextBtn) {
      this.listen(this.nextBtn, 'click', function () {
        self.step(1);
      });
    }
    this.thumbs.forEach(function (thumb) {
      self.listen(thumb, 'click', function () {
        self.goTo(parseInt(thumb.getAttribute('data-pgm-index'), 10) || 0);
      });
    });

    this.listen(this.root, 'keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (self.count() < 2 || !self.isCarousel()) return;
      var t = e.target;
      // Leave arrow keys to native controls that use them.
      if (t.closest('video, iframe, model-viewer, input, select, textarea, dialog')) return;
      e.preventDefault();
      self.step(e.key === 'ArrowLeft' ? -1 : 1);
    });

    // Keep the snapped slide aligned when the viewport width changes.
    var onResize = function () {
      if (self.isCarousel()) self.goTo(self.active, true);
    };
    var resizeTimer;
    this.listen(window, 'resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(onResize, 120);
    });
    var onBreakpoint = function () {
      self.observe();
      onResize();
    };
    onMediaChange(DESKTOP, onBreakpoint);
    this.cleanups.push(function () {
      offMediaChange(DESKTOP, onBreakpoint);
    });
  };

  /* ---------- active-slide tracking ---------- */

  Gallery.prototype.bindObserver = function () {
    if (!('IntersectionObserver' in window) || this.count() < 2) return;
    var self = this;
    this.observe = function () {
      if (self.io) self.io.disconnect();
      self.ratios = [];
      self.io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var k = self.slides.indexOf(entry.target);
            self.ratios[k] = entry.intersectionRatio;
            // Media that has left the viewer stops playing.
            if (entry.intersectionRatio < 0.5) pauseMedia(entry.target);
          });
          if (!self.isCarousel() || self.dragging || self.locked) return;
          var best = -1;
          var bestRatio = 0.5;
          self.ratios.forEach(function (r, k) {
            if (r > bestRatio) {
              bestRatio = r;
              best = k;
            }
          });
          if (best !== -1 && best !== self.active) self.setActive(best);
        },
        { root: self.isCarousel() ? self.viewport : null, threshold: [0, 0.25, 0.5, 0.75, 1] }
      );
      self.slides.forEach(function (s) {
        self.io.observe(s);
      });
    };
    this.observe();
    this.cleanups.push(function () {
      if (self.io) self.io.disconnect();
    });
  };
  Gallery.prototype.observe = function () {};

  /* ---------- mouse drag (desktop carousel) ---------- */

  Gallery.prototype.bindDrag = function () {
    if (this.count() < 2) return;
    var self = this;
    var vp = this.viewport;
    var startX = 0;
    var startScroll = 0;
    var dx = 0;
    var down = false;
    var suppressClick = false;

    function move(e) {
      if (!down) return;
      dx = e.clientX - startX;
      if (!self.dragging && Math.abs(dx) > 6) {
        self.dragging = true;
        vp.classList.add('is-dragging');
        self.resetZoom();
      }
      if (self.dragging) vp.scrollLeft = startScroll - dx;
    }

    function up() {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (!down) return;
      down = false;
      if (!self.dragging) return;

      suppressClick = true;
      var w = vp.clientWidth || 1;
      var from = Math.round(startScroll / w);
      var target = from;
      // A deliberate flick of 12% of the width is enough to change slide.
      if (Math.abs(dx) > w * 0.12) target = from + (dx < 0 ? 1 : -1);
      target = Math.max(0, Math.min(self.count() - 1, target));

      vp.scrollTo({ left: target * w, behavior: behavior() });
      self.setActive(target);
      // Snap stays off until the glide lands, or it would jump instead.
      afterScroll(vp, function () {
        vp.classList.remove('is-dragging');
        self.dragging = false;
      });
    }

    this.listen(vp, 'pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0 || !self.isCarousel()) return;
      if (e.target.closest('video, iframe, model-viewer')) return;
      down = true;
      dx = 0;
      startX = e.clientX;
      startScroll = vp.scrollLeft;
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    });

    // A drag that ends over an image must not also open the lightbox.
    this.listen(
      vp,
      'click',
      function (e) {
        if (!suppressClick) return;
        suppressClick = false;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );
    this.listen(vp, 'dragstart', function (e) {
      e.preventDefault();
    });
  };

  /* ---------- hover zoom ---------- */

  Gallery.prototype.bindZoom = function () {
    if (!this.zoomEnabled) return;
    var self = this;
    var frames = this.root.querySelectorAll('.pgm__media--image');
    frames.forEach(function (frame) {
      var img = frame.querySelector('.pgm__img');
      if (!img) return;
      function origin(e) {
        var r = frame.getBoundingClientRect();
        var x = ((e.clientX - r.left) / r.width) * 100;
        var y = ((e.clientY - r.top) / r.height) * 100;
        img.style.transformOrigin = x.toFixed(2) + '% ' + y.toFixed(2) + '%';
      }
      self.listen(frame, 'pointerenter', function (e) {
        if (e.pointerType !== 'mouse' || !FINE_POINTER.matches || self.dragging) return;
        origin(e);
        frame.classList.add('is-zooming');
      });
      self.listen(frame, 'pointermove', function (e) {
        if (e.pointerType !== 'mouse' || !FINE_POINTER.matches) return;
        if (self.dragging) return;
        origin(e);
        if (!frame.classList.contains('is-zooming')) frame.classList.add('is-zooming');
      });
      self.listen(frame, 'pointerleave', function () {
        frame.classList.remove('is-zooming');
      });
    });
  };

  Gallery.prototype.resetZoom = function (scope) {
    (scope || this.root).querySelectorAll('.is-zooming').forEach(function (el) {
      el.classList.remove('is-zooming');
    });
  };

  /* ---------- lightbox trigger ---------- */

  Gallery.prototype.bindOpen = function () {
    var self = this;
    this.root.querySelectorAll('[data-pgm-open]').forEach(function (btn) {
      self.listen(btn, 'click', function () {
        if (!self.lightbox.dialog) return;
        self.resetZoom();
        self.lightbox.open(btn.getAttribute('data-media-id'), btn);
      });
    });
  };

  /* ---------- variant changes ---------- */

  Gallery.prototype.readVariantMap = function () {
    var el = this.root.querySelector('[data-pgm-variant-media]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      return {};
    }
  };

  Gallery.prototype.currentVariantId = function () {
    var scope = this.section;
    var input =
      scope.querySelector('[data-product-form] input[name="id"]') ||
      scope.querySelector('form[action*="/cart/add"] input[name="id"]');
    return input ? input.value : null;
  };

  Gallery.prototype.showVariant = function (variantId, instant) {
    var mediaId = variantId ? this.variantMedia[String(variantId)] : null;
    if (mediaId === null || mediaId === undefined) return;
    var index = this.indexOfMedia(mediaId);
    if (index === -1 || index === this.active) return;
    this.goTo(index, instant);
  };

  // The variant-picker radios live outside the <form> (linked by form=""), so
  // the listener sits on the section root, which contains both. One tick lets
  // product-info-new.js write the new id into input[name="id"] first.
  Gallery.prototype.bindVariants = function () {
    if (!this.section) return;
    var self = this;
    this.listen(this.section, 'change', function (e) {
      if (self.root.contains(e.target)) return;
      setTimeout(function () {
        self.showVariant(self.currentVariantId());
      }, 0);
    });
  };

  // A shared link with ?variant= opens on that variant's image.
  Gallery.prototype.jumpToUrlVariant = function () {
    var id = null;
    try {
      id = new URL(window.location.href).searchParams.get('variant');
    } catch (e) {}
    if (id) this.showVariant(id, true);
  };

  /* ---------- grid mode: sticky buy box that never hides its bottom ---------- */

  Gallery.prototype.bindStickyBuyBox = function () {
    if (!this.isGrid) return;
    var box = this.root.nextElementSibling;
    while (box && box.tagName !== 'DIV') box = box.nextElementSibling;
    if (!box) return;
    var section = this.section;
    function update() {
      var gap = 24;
      var top = Math.min(gap, window.innerHeight - box.offsetHeight - gap);
      section.style.setProperty('--pgm-sticky-top', top + 'px');
    }
    update();
    this.listen(window, 'resize', update);
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(update);
      ro.observe(box);
      this.cleanups.push(function () {
        ro.disconnect();
      });
    }
  };

  Gallery.prototype.destroy = function () {
    this.cleanups.forEach(function (fn) {
      fn();
    });
    this.cleanups = [];
    if (this.lightbox) this.lightbox.destroy();
  };

  /* ======================================================================
   * Lightbox — native <dialog> + showModal()
   * ==================================================================== */

  function Lightbox(root, gallery) {
    this.gallery = gallery;
    this.dialog = root.querySelector('[data-pgm-lightbox]');
    this.cleanups = [];
    if (!this.dialog) return;

    var d = this.dialog;
    this.viewport = d.querySelector('[data-pgm-lb-viewport]');
    this.slides = Array.prototype.slice.call(d.querySelectorAll('[data-pgm-lb-slide]'));
    this.current = d.querySelector('[data-pgm-lb-current]');
    this.prevBtn = d.querySelector('[data-pgm-lb-prev]');
    this.nextBtn = d.querySelector('[data-pgm-lb-next]');
    this.closeBtn = d.querySelector('[data-pgm-lb-close]');
    this.index = 0;
    this.opener = null;
    this.closing = false;

    this.bind();
  }

  Lightbox.prototype.listen = Gallery.prototype.listen;

  Lightbox.prototype.bind = function () {
    var self = this;
    var d = this.dialog;

    if (this.closeBtn) {
      this.listen(this.closeBtn, 'click', function () {
        self.close();
      });
    }
    if (this.prevBtn) {
      this.listen(this.prevBtn, 'click', function () {
        self.go(self.index - 1);
      });
    }
    if (this.nextBtn) {
      this.listen(this.nextBtn, 'click', function () {
        self.go(self.index + 1);
      });
    }

    // Esc: animate out instead of the native instant close.
    this.listen(d, 'cancel', function (e) {
      e.preventDefault();
      self.close();
    });
    // Whatever closed it (ours, or the browser), always clean up.
    this.listen(d, 'close', function () {
      self.afterClose();
    });

    this.listen(d, 'keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        self.go(self.index - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        self.go(self.index + 1);
      }
    });

    var ticking = false;
    this.listen(this.viewport, 'scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        var w = self.viewport.clientWidth || 1;
        var i = Math.round(self.viewport.scrollLeft / w);
        if (i !== self.index) {
          self.unzoom();
          self.index = i;
          self.render();
        }
      });
    });

    // Desktop: click toggles 2x at the click point; moving the mouse pans.
    this.slides.forEach(function (slide) {
      var frame = slide.querySelector('[data-pgm-lb-frame]');
      var img = slide.querySelector('.pgm-lb__img');
      if (!frame || !img) return;
      function origin(e) {
        var r = frame.getBoundingClientRect();
        var x = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
        var y = Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100));
        img.style.transformOrigin = x.toFixed(2) + '% ' + y.toFixed(2) + '%';
      }
      self.listen(img, 'click', function (e) {
        if (!FINE_POINTER.matches) return;
        if (slide.classList.contains('is-zoomed')) {
          self.unzoom();
        } else {
          origin(e);
          slide.classList.add('is-zoomed');
          d.classList.add('is-zoomed');
        }
      });
      self.listen(frame, 'mousemove', function (e) {
        if (slide.classList.contains('is-zoomed')) origin(e);
      });
    });
  };

  Lightbox.prototype.unzoom = function () {
    if (!this.dialog) return;
    this.dialog.classList.remove('is-zoomed');
    this.slides.forEach(function (s) {
      s.classList.remove('is-zoomed');
    });
  };

  Lightbox.prototype.go = function (i, instant) {
    var n = this.slides.length;
    if (!n) return;
    i = Math.max(0, Math.min(n - 1, i));
    this.unzoom();
    this.index = i;
    this.viewport.scrollTo({
      left: i * this.viewport.clientWidth,
      behavior: instant ? 'auto' : behavior()
    });
    this.render();
  };

  Lightbox.prototype.render = function () {
    var n = this.slides.length;
    if (this.current) this.current.textContent = String(this.index + 1);
    if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.index >= n - 1;
  };

  Lightbox.prototype.open = function (mediaId, opener) {
    var d = this.dialog;
    if (!d || d.open || typeof d.showModal !== 'function') return;
    var self = this;
    var index = 0;
    for (var k = 0; k < this.slides.length; k++) {
      if (String(this.slides[k].getAttribute('data-media-id')) === String(mediaId)) {
        index = k;
        break;
      }
    }
    this.opener = opener || null;
    this.closing = false;

    // Lock page scroll; the dialog itself scrolls horizontally.
    var html = document.documentElement;
    this.prevOverflow = html.style.overflow;
    html.style.overflow = 'hidden';

    d.showModal();
    // Eager-load the one being opened (and its neighbours) at full size.
    [index - 1, index, index + 1].forEach(function (i) {
      var s = self.slides[i];
      var img = s && s.querySelector('img');
      if (img) img.loading = 'eager';
    });
    this.go(index, true);
    // showModal focuses the first control; put focus on the viewer instead,
    // so arrow keys work at once and Esc is still one key away.
    this.viewport.focus({ preventScroll: true });

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        d.classList.add('is-open');
      });
    });
  };

  Lightbox.prototype.close = function () {
    var d = this.dialog;
    if (!d || !d.open || this.closing) return;
    this.closing = true;
    d.classList.remove('is-open');
    if (REDUCED.matches) {
      d.close();
      return;
    }
    var finished = false;
    function done() {
      if (finished) return;
      finished = true;
      d.removeEventListener('transitionend', onEnd);
      if (d.open) d.close();
    }
    function onEnd(e) {
      if (e.target === d && e.propertyName === 'opacity') done();
    }
    d.addEventListener('transitionend', onEnd);
    setTimeout(done, 320);
  };

  Lightbox.prototype.afterClose = function () {
    this.closing = false;
    this.unzoom();
    this.dialog.classList.remove('is-open');
    document.documentElement.style.overflow = this.prevOverflow || '';
    if (this.opener && document.contains(this.opener)) {
      this.opener.focus({ preventScroll: true });
    }
    this.opener = null;
  };

  Lightbox.prototype.destroy = function () {
    if (this.dialog && this.dialog.open) this.dialog.close();
    this.cleanups.forEach(function (fn) {
      fn();
    });
    this.cleanups = [];
  };

  /* ======================================================================
   * Boot + theme editor
   * ==================================================================== */

  var instances = new WeakMap();

  function init(scope) {
    (scope || document).querySelectorAll('[data-pgm]').forEach(function (el) {
      if (instances.has(el)) return;
      instances.set(el, new Gallery(el));
    });
  }

  function teardown(scope) {
    if (!scope) return;
    scope.querySelectorAll('[data-pgm]').forEach(function (el) {
      var g = instances.get(el);
      if (g) g.destroy();
      instances.delete(el);
    });
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    teardown(e.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }
})();
